import React from "react";
import { ArrowRight } from "lucide-react";

type Cta = { label: string; onClick: () => void };

type LandingHeroProps = {
  image: string;
  imageAlt: string;
  badge: string;
  titleLines: string[];
  /** Figma uses 60px on Work and 72px on Stay */
  titleSize?: "lg" | "xl";
  lead: string;
  body?: string;
  primaryCta: Cta;
  secondaryCta: Cta;
  /** Desktop height of the hero image from the design */
  desktopHeight: number;
  /** Search bar rendered 24px up over the bottom edge of the hero */
  search?: React.ReactNode;
  /** Overlay strengths and lead opacity vary per page in the design (0 = no overlay) */
  shade?: { bottom: number; side: number; lead: number };
  /** Optional row of outlined pills under the lead (Play) */
  pills?: string[];
};

/** Full-bleed photo hero used by the category landing pages (Work, Stay, …). */
export default function LandingHero({
  image,
  imageAlt,
  badge,
  titleLines,
  titleSize = "lg",
  lead,
  body,
  primaryCta,
  secondaryCta,
  desktopHeight,
  search,
  shade = { bottom: 0.8, side: 0.5, lead: 0.75 },
  pills,
}: LandingHeroProps) {
  const titleClass =
    titleSize === "xl"
      ? "text-[44px] leading-[1.05] tracking-[-1.8px] sm:text-[72px] sm:leading-[73.44px]"
      : "text-[40px] leading-[1.05] tracking-[-1.5px] sm:text-[60px] sm:leading-[63px]";

  return (
    <>
      <section
        className="relative flex min-h-[640px] items-end overflow-hidden lg:h-[var(--hero-height)]"
        style={{ "--hero-height": `${desktopHeight}px` } as React.CSSProperties}
      >
        <img src={image} alt={imageAlt} className="absolute inset-0 size-full object-cover" />
        {shade.bottom > 0 && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(0deg, rgba(10,10,10,${shade.bottom}) 0%, rgba(10,10,10,${Math.min(0.3, shade.bottom)}) 50%, rgba(10,10,10,0) 100%)`,
            }}
          />
        )}
        {shade.side > 0 && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(90deg, rgba(10,10,10,${shade.side}) 0%, rgba(10,10,10,0) 100%)`,
            }}
          />
        )}

        <div className="relative mx-auto w-full max-w-[1440px] px-[16px] pb-24 pt-32 sm:px-8 lg:pb-20">
          <div className="max-w-[672px]">
            <span className="inline-block rounded-full bg-[#017bef] px-[16px] py-1.5 text-[12px] font-bold uppercase leading-4 tracking-[1.2px] text-white">
              {badge}
            </span>
            <h1 className={`pt-[12px] font-bold text-white ${titleClass}`}>
              {titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {index > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </h1>
            <p
              className="max-w-[576px] pt-6 text-[18px] leading-[29.25px]"
              style={{ color: `rgba(255,255,255,${shade.lead})` }}
            >
              {lead}
            </p>
            {body && <p className="max-w-[576px] pt-[20px] text-[16px] leading-[26px] text-white/55">{body}</p>}
            {pills && (
              <div className="flex flex-wrap gap-2 pt-8">
                {pills.map(pill => (
                  <span
                    key={pill}
                    className="rounded-full border-[1px] border-solid border-white/20 px-[16px] py-1.5 text-[12px] font-semibold leading-4 text-white/70"
                  >
                    {pill}
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-[16px] pt-8">
              <button
                type="button"
                onClick={primaryCta.onClick}
                className="inline-flex items-center gap-2 rounded-full bg-[#017bef] px-7 py-3.5 text-[14px] font-bold leading-5 text-white drop-shadow-[0_4px_8px_rgba(1,123,239,0.3)] transition-colors hover:bg-[#0062c0]"
              >
                {primaryCta.label}
                <ArrowRight className="size-4" />
              </button>
              <button
                type="button"
                onClick={secondaryCta.onClick}
                className="rounded-full border-[1px] border-solid border-white/30 px-7 py-3.5 text-[14px] font-semibold leading-5 text-white transition-colors hover:bg-white/10"
              >
                {secondaryCta.label}
              </button>
            </div>
          </div>
        </div>
      </section>

      {search && (
        <div className="relative z-10 mx-auto -mt-[24px] w-full max-w-[1440px] px-[16px] sm:px-8">{search}</div>
      )}
    </>
  );
}
